import { alpha } from "@mui/material/styles";

import { type ComponentOverrides } from "theme/theme.types";

import { AUTOCOMPLETE_POPUP_ICON } from "theme/components/select-glyphs";

import { colors, shadows } from "design-tokens";

/** MUI anchors the popper flush against the input, so the two would read as one surface. */
const SELECT_DROPDOWN_GAP = 8;

/**
 * The selected option's wash is mixed from a translucent black, not a `neutrals` step — that is what
 * keeps a hovered selected row readable as one tint over another.
 */
const SELECT_OPTION_TINT = colors.neutrals[950];

/**
 * Through popper's `offset` rather than a `marginTop`: the offset follows the *placement* axis, so
 * a list that flips above the control keeps the same gap. `slotProps` merges per slot, so a
 * consumer passing its own `popper.modifiers` would replace this array. None does today.
 */
export const MuiAutocomplete: ComponentOverrides["MuiAutocomplete"] = {
  defaultProps: {
    popupIcon: AUTOCOMPLETE_POPUP_ICON,
    slotProps: {
      popper: {
        modifiers: [{ name: "offset", options: { offset: [0, SELECT_DROPDOWN_GAP] } }],
      },
    },
  },
  styleOverrides: {
    /**
     * Without this the list falls to `Paper`'s default rung (1, i.e. `sm`) and reads flatter than the
     * popover panel beside it. Every floating surface names `lg` itself — see `theme/shadows.ts`.
     */
    paper: { boxShadow: shadows.lg },

    /**
     * MUI hides the clear icon until the control is hovered or focused; Figma draws it at rest, like
     * `ui/text-field`'s. This slot's own `visibility: hidden` sits in the same class, earlier, so an
     * equal-specificity override wins on order. MUI still renders it only with a value, unless
     * disabled or read-only.
     */
    clearIndicator: ({ theme }) => ({
      visibility: "visible",
      color: theme.palette.primary.main,
    }),
    popupIndicator: ({ theme }) => ({ color: theme.palette.text.primary }),

    /**
     * MUI washes the selected option with `primary.main`; same arithmetic, black instead of aqua.
     *
     * **On `listbox`, not the `option` slot.** MUI keeps its option rules inside
     * `AutocompleteListbox`, so the same selector shape from the same slot lands at equal
     * specificity and later in source. The `option` slot resolves onto the *popper*, whose class
     * is inserted first, being the parent.
     */
    listbox: ({ theme }) => ({
      "& .MuiAutocomplete-option": {
        '&[aria-selected="true"]': {
          backgroundColor: alpha(SELECT_OPTION_TINT, theme.palette.action.selectedOpacity),
          "&.Mui-focused": {
            backgroundColor: alpha(
              SELECT_OPTION_TINT,
              theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity,
            ),
          },
        },
      },
    }),
  },
};
